import {useState} from "react";
import CreateAnotherCheckBox from "@/components/CreateAnotherCheckBox";
import MultipleLineError from "@/components/MultipleLineError";
import {TextField} from "@mui/material";
import {
    useBoolState,
    FormattedFormRow,
    FormContainer,
    FormDialogActionButtons,
    DialogActions,
    DialogContent,
} from "@dvrt/graphql-widgets";
import {Link} from "@/components/widgets";
import {defineMessage} from "react-intl";
import {showNotification} from "@/actions";
import {useFormGuard} from "@/utils/formGuard";
import ErrorHelper from "@/utils/ErrorHelper";
import {useForm, Form} from "@/utils/Form";
import {CreateIvrMutation, CreateIvrMutationVariables, useCreateIvrMutation} from "./queries.generated";
import EndpointCreateDialog, {EndpointLimitInfo, QuotaInfo} from "@/components/EndpointCreateDialog";

type ContentProps = {
    domainId: number;
    onSuccess: () => void;
    close: () => void;
    quotaInfo: QuotaInfo;
};

type CreateIvrDialogProps = {
    isOpen: boolean;
} & ContentProps;

const successNotification = (form: Form<CreateIvrMutation, CreateIvrMutationVariables>) => {
    const url = `/domains/${form.values.domainId}/ivrs/${form.result.data.createIvr.id}/`;
    return showNotification(
        defineMessage({
            id: "ivr.notification.created",
            defaultMessage: "Auto attendant {link} has been successfully created.",
        }),
        "success",
        {
            link: <Link to={url}>{form.values.identifier ?? form.values.e164}</Link>,
        },
        10000,
    );
};

const DialogForm = ({domainId, onSuccess, close, quotaInfo}: ContentProps) => {
    const form = useForm<CreateIvrMutation, CreateIvrMutationVariables>(
        useCreateIvrMutation,
        "createIvr",
        {
            domainId: domainId,
            name: "",
            e164: "",
            identifier: "",
        },
        successNotification,
    );
    const errorHelper = new ErrorHelper(form.errors);
    const [createAnother, setCreateAnother] = useState(false);

    useFormGuard(form.hasChanged);

    const [enabled, enable, disable] = useBoolState(true);

    function handleClose() {
        form.reset();
        close();
    }

    async function submit() {
        disable();

        const res = await form.submit();

        enable();

        if (!res) return;

        onSuccess();

        if (createAnother) form.reset();
        else handleClose();
    }

    return (
        <>
            <DialogContent>
                <FormContainer>
                    <EndpointLimitInfo quotaInfo={quotaInfo} errors={errorHelper.fieldErrors("limit")} />
                    <FormattedFormRow label={defineMessage({id: "e164", defaultMessage: "E.164"})}>
                        <TextField
                            variant="standard"
                            disabled={!enabled}
                            margin="dense"
                            fullWidth
                            value={form.values.e164}
                            onChange={(event) => form.updateValues({e164: event.target.value})}
                            error={errorHelper.hasError("e164")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("e164")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "identifier", defaultMessage: "Identifier"})} required>
                        <TextField
                            variant="standard"
                            disabled={!enabled}
                            required
                            fullWidth
                            margin="dense"
                            value={form.values.identifier}
                            onChange={(event) => form.updateValues({identifier: event.target.value})}
                            error={errorHelper.hasError("identifier")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("identifier")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "name", defaultMessage: "Name"})} required>
                        <TextField
                            variant="standard"
                            disabled={!enabled}
                            required
                            fullWidth
                            margin="dense"
                            value={form.values.name}
                            onChange={(event) => form.updateValues({name: event.target.value})}
                            error={errorHelper.hasError("name")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("name")} />}
                        />
                    </FormattedFormRow>
                </FormContainer>
            </DialogContent>
            <DialogActions style={{paddingLeft: 60}}>
                <CreateAnotherCheckBox state={createAnother} setState={setCreateAnother} />
                <FormDialogActionButtons
                    mainAction={submit}
                    secondaryAction={() => form.reset()}
                    cancel={handleClose}
                    enabled={form.hasChanged}
                />
            </DialogActions>
        </>
    );
};

const CreateIvrDialog = ({close, domainId, isOpen, onSuccess, quotaInfo}: CreateIvrDialogProps) => {
    return (
        <EndpointCreateDialog
            title={defineMessage({id: "new_ivr", defaultMessage: "New auto attendant"})}
            isOpen={isOpen}
            closeDialog={close}
            quotaInfo={quotaInfo}
        >
            <DialogForm domainId={domainId} onSuccess={onSuccess} close={close} quotaInfo={quotaInfo} />
        </EndpointCreateDialog>
    );
};
export default CreateIvrDialog;
